import type { ShowWithPrice, VodWithPrice } from '~/shared/interfaces'

import { api, GlobalSearchMediaTypes } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { not, or } from 'patronum'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { formatLiveEventCardWithPrice } from '~/entities/live-events/events'
import { resolveShowNewLabel } from '~/entities/shows'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { profilesFeaturesModel } from '~/features/profiles'
import { searchAutocompleteModel } from '~/features/search-autocomplete'
import { getNextPage } from '~/shared/effector'
import { checkIsSearchQueryValid } from '~/shared/helpers'
import {
  extendMediaItemByLabelPrice,
  extendMediaItemByLabelPriceTvChannel,
  resolveNewLabel,
} from '~/shared/tools'
import { ContentType, initRecommendedVodsPage, searchInit } from '../root/model'
import { leftRegularSearchPage } from '../shared/regularSearchPageState'
import { resetSearchOnProfileSwitch } from '../shared/resetOnProfileSwitch'
import { model as search } from '../shared/searchField'

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

// Constants

const INIT_GRID_ELEMENTS_COUNT = 11

// Events

export const left = createEvent()
export const resetResultsPageUiState = createEvent()

sample({
  clock: left,
  target: [leftRegularSearchPage, searchAutocompleteModel.resetAutocomplete],
})

// Effects

const getSearchEventsFx = attach({
  effect: api.globalSearch.loadSearchEventsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchTvChannelsFx = attach({
  effect: api.globalSearch.loadSearchTvChannelsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchTvShowsFx = attach({
  effect: api.globalSearch.loadSearchTvShowsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const getSearchVodsFx = attach({
  effect: api.globalSearch.loadSearchVodsFx,
  source: search.$searchValue,
  mapParams: ({ count, cursor }, q) => ({ count, cursor, q }),
})

const $content = combine(
  api.globalSearch.$searchResults,
  ssePurchasesModel.$liveEvent,
  ssePurchasesModel.$channel,
  ssePurchasesModel.$tvShow,
  ssePurchasesModel.$vod,
  (data, adjustEvent, adjustChannel, adjustShow, adjustVod) => ({
    liveEvents: data?.liveEvents?.map((liveEvent) => adjustEvent(liveEvent)),
    tvChannels: data?.tvChannels?.map((liveEvent) => adjustChannel(liveEvent)),
    tvShows: data?.tvShows?.map((liveEvent) => adjustShow(liveEvent)),
    vods: data?.vods?.map((liveEvent) => adjustVod(liveEvent)),
  })
)

export const $contentFormatted = combine(
  $content,
  i18n.$t,
  (content, { t }) => [
    {
      contentType: GlobalSearchMediaTypes.LIVE_EVENT,
      title: t('event_other'),
      list: content?.liveEvents?.map(formatLiveEventCardWithPrice),
    },
    {
      contentType: GlobalSearchMediaTypes.TV_CHANNEL,
      title: t('live_channels'),
      list: content?.tvChannels?.map((tvChannel) => {
        const formatted = extendMediaItemByLabelPriceTvChannel(tvChannel)

        return { ...formatted, newLabel: resolveNewLabel(formatted, t) }
      }),
    },
    {
      contentType: GlobalSearchMediaTypes.TV_SHOW,
      title: t('series'),
      list: content?.tvShows?.map((tvShow) => {
        const formatted = extendMediaItemByLabelPrice(tvShow) as ShowWithPrice

        return {
          ...formatted,
          newLabel: resolveShowNewLabel(formatted, t),
        }
      }),
    },
    {
      contentType: GlobalSearchMediaTypes.VOD,
      title: t('movies'),
      list: content?.vods?.map((vod) => {
        const formatted = extendMediaItemByLabelPrice(vod) as VodWithPrice

        return {
          ...formatted,
          newLabel: resolveNewLabel(formatted, t),
        }
      }),
    },
  ]
)

const $hasResults = $content.map((content) =>
  Object.values(content).some((value) => value?.length)
)

const $isRequestsPending = api.globalSearch.getSearchFx.pending

const $noResultsMessage = createStore('')
const $lastSearchInitParams = createStore<{
  contentType?: ContentType
  query: URLSearchParams
} | null>(null).on(searchInit, (_, params) => params)

sample({
  clock: $content,
  source: {
    value: search.$searchValue,
    hasResults: $hasResults,
    isRequestsPending: $isRequestsPending,
    i18n: i18n.$t,
  },
  filter: ({ hasResults, isRequestsPending }) =>
    !hasResults && !isRequestsPending,
  fn: ({ value, i18n: { t } }) =>
    t('search_no_results', {
      request: value,
    }),
  target: $noResultsMessage,
})

sample({
  clock: resetResultsPageUiState,
  fn: () => '',
  target: $noResultsMessage,
})

sample({
  clock: resetResultsPageUiState,
  fn: () => false,
  target: setContentLoading,
})

sample({
  clock: resetSearchOnProfileSwitch,
  target: resetResultsPageUiState,
})

// Connections

/* Reset on searchInit is necessary so that old $searchResults should not be saved */
sample({
  clock: searchInit,
  fn: () => null,
  target: api.globalSearch.$searchResults,
})

sample({
  clock: searchInit,
  filter: ({ query }) =>
    query.has('q') && checkIsSearchQueryValid(query.get('q') || ''),
  fn: ({ query }) => ({
    q: query.get('q') || '',
    count: INIT_GRID_ELEMENTS_COUNT,
    typeOfSearch: query.get('type') || '',
  }),
  target: api.globalSearch.getSearchFx,
})

/********** Skeleton logics starts ***********/

const $isSearchByContentTypePending = or(
  api.globalSearch.loadSearchEventsFx.pending,
  api.globalSearch.loadSearchTvChannelsFx.pending,
  api.globalSearch.loadSearchTvShowsFx.pending,
  api.globalSearch.loadSearchVodsFx.pending
)

sample({
  clock: api.globalSearch.getSearchFx.pending,
  filter: not($isSearchByContentTypePending),
  target: setContentLoading,
})

/********** Skeleton logics ends ***********/

// Add to favorite

sample({
  clock: $noResultsMessage,
  source: {
    searchInitParams: $lastSearchInitParams,
    isActiveKidsProfile: profilesFeaturesModel.$isActiveKidsProfile,
  },
  filter: ({ isActiveKidsProfile, searchInitParams }, message) =>
    Boolean(message) && !isActiveKidsProfile && searchInitParams != null,
  fn: ({ searchInitParams }) => searchInitParams!,
  target: initRecommendedVodsPage,
})

// Load more content

const loadMoreEvents = getNextPage(
  api.globalSearch.$searchEventsPageInfo,
  getSearchEventsFx
)

const loadMoreTvChannels = getNextPage(
  api.globalSearch.$searchTvChannelsPageInfo,
  getSearchTvChannelsFx
)

const loadMoreTvShows = getNextPage(
  api.globalSearch.$searchTvShowsPageInfo,
  getSearchTvShowsFx
)

const loadMoreVods = getNextPage(
  api.globalSearch.$searchVodsPageInfo,
  getSearchVodsFx
)

register($contentFormatted, '$search/contentFormatted')
register($isRequestsPending, '$search/isRequestsPending')
register($hasResults, '$search/hasResults')
register($noResultsMessage, '$search/noResultsMessage')
register(api.globalSearch.$searchVodsPageInfo, '$search/vodsPageInfo')
register(api.globalSearch.$searchTvShowsPageInfo, '$search/allTvShowsPageInfo')
register(
  api.globalSearch.$searchTvChannelsPageInfo,
  '$search/allTvChannelsPageInfo'
)
register(api.globalSearch.$searchEventsPageInfo, '$search/searchEventsPageInfo')
register(loadMoreVods, 'search/loadMoreVods')
register(loadMoreTvShows, 'search/loadMoreTvShows')
register(loadMoreTvChannels, 'search/loadMoreTvChannels')
register(loadMoreEvents, 'search/loadMoreEvents')
